import { styled, Typography, Button, css, keyframes } from '@mui/material';
import { theme, breakpoints } from 'assets/themes/zizzi';
import { fadeIn, scaleUp, scaleY } from 'assets/styles/animations';
import ProgressionTick from 'assets/icons/ProgressionTick';
import ChocolateDessert from 'assets/icons/ChocolateDessert';
import GarlicBread from 'assets/icons/GarlicBread';
import Primavera from 'assets/icons/Primavera';
import Drinks from 'assets/icons/Drinks';
import Feast from 'assets/icons/Feast';
import Pasta from 'assets/icons/Pasta';
import Modal from 'components/Modal';

const tickSizeXS = 34;
const tickSizeSM = 42;
const themeBlue = theme.palette.primary.main;
const textColor = theme.palette.white;

const { galaxyFold, iphoneSE } = breakpoints;

const centeredStyles = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const animationSettings = '1s cubic-bezier(0, -0.10, 0, 1.5) 1s forwards';

const animatePrimavera = keyframes`
  from {
    bottom: -10%;
    left: -30%;
    transform: rotate(-45deg);
  }
  to {
    bottom: -3%;
    left: -6%;
    transform: rotate(0deg);
  }
`;

const animateGarlicBread = keyframes`
  from {
    left: -30%;
    top: -10%;
    transform: rotate(-45deg);
  }
  to {
    left: 4%;
    top: 5%;
    transform: rotate(0deg);
  }
`;

const animateChocolateDessert = keyframes`
  from {
    right: -30%;
    top: -10%;
    transform: rotate(45deg);
  }
  to {
    right: -3%;
    top: -4%;
    transform: rotate(0deg);
  }
`;

const animateFeast = keyframes`
  from {
    bottom: -30%;
    right: -30%;
    transform: rotate(45deg);
  }
  to {
    bottom: -21%;
    right: -6%;
    transform: rotate(0deg);
  }
`;

const animateDrinks = keyframes`
  0% {
    bottom: -100%;
  }
  100% {
    bottom: -36%;
  }
`;

const animatePasta = keyframes`
  from {
    bottom: -60%;
    left: -20%;
    transform: rotate(45deg);
  }
  to {
    bottom: -45%;
    left: 4%;
    transform: rotate(0deg);
  }
`;

export const StyledModal = styled(Modal)`
  .MuiDialog-container {
    background-color: white;
  }

  .MuiPaper-root {
    overflow: hidden;

    ${theme.breakpoints.up('sm')} {
      max-width: 880px;
    }
  }
`;

export const Wrapper = styled('div')`
  ${centeredStyles};
  animation: ${fadeIn} ${animationSettings};
  height: 100%;
  justify-content: space-between;
  opacity: 0;
  z-index: 0;

  ${theme.breakpoints.up('sm')} {
    padding-top: 5rem;
  }

  ${theme.breakpoints.up('md')} {
    height: 800px;
    padding-top: 0;
  }
`;

export const Header = styled('div')`
  ${centeredStyles};
  height: 55%;
  padding: 1rem;
  position: relative;

  ${theme.breakpoints.up('sm')} {
    margin-bottom: 10%;
  }
`;

export const Achievement = styled('div')`
  ${centeredStyles};
  animation: ${scaleY} ${animationSettings};
  background-color: ${themeBlue};
  border-radius: 11px;
  margin-bottom: 1.75rem;
  padding: 1rem;
  position: relative;
  min-width: fit-content;
  width: 100px;
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14),
    0px 1px 5px 0px rgba(0, 0, 0, 0.12);

  ${theme.breakpoints.up('sm')} {
    margin-top: 3rem;
    width: 135px;
  }
`;

export const Tick = styled(ProgressionTick)`
  height: ${tickSizeXS}px;
  right: -${tickSizeXS / 2}px;
  width: ${tickSizeXS}px;
  position: absolute;
  top: -11px;
  border-radius: 50%;
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14),
    0px 1px 5px 0px rgba(0, 0, 0, 0.12);

  ${theme.breakpoints.up('sm')} {
    height: ${tickSizeSM}px;
    right: -${tickSizeSM / 2}px;
    width: ${tickSizeSM}px;
  }
`;

export const Text = styled(Typography)`
  color: ${textColor};
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.5px;

  &:first-of-type {
    font-weight: 400;
    letter-spacing: 0;
    margin-bottom: 0.3rem;
  }

  ${theme.breakpoints.up('sm')} {
    font-size: 30px;

    &:first-of-type {
      font-weight: 400;
      letter-spacing: 0;
      margin-bottom: 0.3rem;
    }
  }
`;

export const Title = styled(Typography)`
  animation: ${scaleUp} ${animationSettings};
  color: ${themeBlue};
  max-width: 80%;
  text-align: center;

  ${theme.breakpoints.up('sm')} {
    max-width: 70%;
  }
`;

export const ImageWrapper = styled('div')`
  height: 100%;
  position: absolute;
  width: 100%;
  z-index: -1;
`;

export const StyledGarlicBread = styled(GarlicBread)`
  animation: ${animateGarlicBread} ${animationSettings};
  left: 4%;
  position: absolute;
  top: 5%;

  @media (max-width: ${galaxyFold}) {
    animation: none;
    left: -16px;
    top: 41px;
  }

  ${theme.breakpoints.up('md')} {
    height: 187px;
    width: 187px;
  }
`;

export const StyledChocolateDessert = styled(ChocolateDessert)`
  animation: ${animateChocolateDessert} ${animationSettings};
  height: 127px;
  width: 155px;
  position: absolute;
  right: -3%;
  top: -4%;

  @media (max-width: ${galaxyFold}) {
    animation: none;
    top: 13px;
    height: 127px;
    width: 97px;
  }

  ${theme.breakpoints.up('md')} {
    height: 187px;
    width: 187px;
  }
`;

export const StyledPrimavera = styled(Primavera)`
  animation: ${animatePrimavera} ${animationSettings};
  bottom: -3%;
  left: -6%;
  position: absolute;

  @media (max-width: ${galaxyFold}) {
    animation: none;
    bottom: -48px;
    width: 100px;
  }

  ${theme.breakpoints.up('md')} {
    height: 242px;
    width: 184px;
  }
`;

export const StyledPasta = styled(Pasta)`
  animation: ${animatePasta} ${animationSettings};
  bottom: -45%;
  left: 4%;
  position: absolute;
  width: 110px;
  height: 177px;

  @media (max-width: ${galaxyFold}) {
    animation: none;
    display: none;
  }

  @media (max-height: ${iphoneSE}) {
    display: none;
  }

  ${theme.breakpoints.up('md')} {
    height: 146px;
    width: 184px;
  }
`;

export const StyledDrinks = styled(Drinks)`
  animation: ${animateDrinks} ${animationSettings};
  bottom: -36%;
  left: 28%;
  position: absolute;
  height: 162px;
  width: 146px;

  @media (max-width: ${galaxyFold}) {
    animation: none;
    display: none;
  }

  @media (max-height: ${iphoneSE}) {
    display: none;
  }

  ${theme.breakpoints.up('sm')} {
    height: 135px;
    width: 164px;
  }

  ${theme.breakpoints.up('md')} {
    height: 167px;
    width: 283px;
  }
`;

export const StyledFeast = styled(Feast)`
  animation: ${animateFeast} ${animationSettings};
  bottom: -21%;
  position: absolute;
  right: -6%;

  @media (max-width: 349px) {
    animation: none;
    bottom: -83px;
    width: 115px;
  }

  ${theme.breakpoints.up('md')} {
    height: 218px;
    width: 229px;
  }
`;

export const Footer = styled('div')`
  ${centeredStyles};
  padding: 1rem;
`;

export const FooterText = styled(Typography)`
  margin-bottom: 1.5rem;
  max-width: 460px;
  text-align: center;
`;

export const StyledButton = styled(Button)`
  ${theme.breakpoints.up('sm')} {
    max-width: 350px;
  }
  ${theme.breakpoints.between(280, 300)} {
    width: 250px;
  }
`;
